(() => {
  'use strict';
  if (window.StoreCatalogCommerce) return;
  const mapRequests = new Map();
  const integer = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
  const error = (code, message = '') => Object.assign(new Error(message || code), {code});
  class CatalogCommerce {
    constructor({root = '/', mapURL, currency = 'PHP'}) {
      this.root = root.endsWith('/') ? root : root + '/';
      this.mapURL = mapURL;
      this.currency = currency;
      this.products = new Map();
      this.loading = new Map();
      this.mapping = null;
      this.cart = null;
    }
    url(path) { return this.root + path; }
    async request(path, data) {
      const response = await fetch(this.url(path), {credentials:'same-origin', cache:'no-store', headers:{'Accept':'application/json', ...(data ? {'Content-Type':'application/json'} : {})}, ...(data ? {method:'POST', body:JSON.stringify(data)} : {})});
      const body = await response.json().catch(() => null);
      if (!response.ok) throw error(response.status === 404 ? 'unavailable' : 'request_failed', typeof body?.description === 'string' ? body.description : typeof body?.message === 'string' ? body.message : '');
      if (!body || typeof body !== 'object') throw error('request_failed');
      return body;
    }
    async loadMap() {
      if (!mapRequests.has(this.mapURL)) mapRequests.set(this.mapURL, fetch(this.mapURL, {credentials:'same-origin'}).then(async response => {
        if (!response.ok) throw error('unavailable');
        const data = await response.json();
        if (!data?.categories || typeof data.categories !== 'object') throw error('unavailable');
        return data;
      }).catch(reason => { mapRequests.delete(this.mapURL); throw reason; }));
      this.mapping = await mapRequests.get(this.mapURL);
      return this.mapping;
    }
    async loadCategory(categoryId, refresh = false) {
      if (!refresh && this.products.has(categoryId)) return this.products.get(categoryId);
      if (!refresh && this.loading.has(categoryId)) return this.loading.get(categoryId);
      const request = (async () => {
        try {
          await this.loadMap();
          const category = this.mapping.categories[categoryId];
          if (!category?.handle) throw error('unavailable');
          const product = await this.request('products/' + encodeURIComponent(category.handle) + '.js');
          if (!Array.isArray(product.variants)) throw error('unavailable');
          const result = {product}; this.products.set(categoryId, result); return result;
        } catch (reason) {
          const result = {error:reason.code === 'unavailable' ? 'unavailable' : 'request_failed'};
          this.products.set(categoryId, result); return result;
        } finally { this.loading.delete(categoryId); }
      })();
      this.loading.set(categoryId, request);
      return request;
    }
    selection(categoryId, sourceProductId, unit) {
      const loaded = this.products.get(categoryId);
      if (!loaded) return {status:'loading'};
      if (loaded.error) return {status:loaded.error};
      const id = integer(this.mapping?.categories?.[categoryId]?.variants?.[sourceProductId]?.[unit]);
      const variant = loaded.product.variants.find(item => Number(item.id) === id);
      if (!id || !variant) return {status:'unavailable'};
      if (!(Number(variant.price) > 0)) return {status:'price_pending', variant};
      if (!variant.available) return {status:'sold_out', variant};
      return {status:'ready', variant};
    }
    money(value, currency = this.currency) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-PH', {style:'currency', currency}).format(Number(value) / 100);
    }
    async getCart() {
      const data = await this.request('cart.js');
      if (!Array.isArray(data.items)) throw error('request_failed');
      this.cart = data; this.currency = data.currency || this.currency;
      this.updateBadge(data.item_count);
      return data;
    }
    updateBadge(count) {
      document.querySelectorAll('[data-wb-cart-count]').forEach(node => { node.textContent = count; });
      const header = document.getElementById('cart-icon-bubble');
      if (header) {
        let badge = header.querySelector('.cart-count-bubble');
        if (!badge) { badge = document.createElement('div'); badge.className = 'cart-count-bubble'; header.append(badge); }
        badge.textContent = count; badge.hidden = !count; badge.style.display = count ? '' : 'none';
      }
      document.dispatchEvent(new CustomEvent('southply:cart-updated', {detail:{cart:this.cart}}));
    }
    async add({categoryId, product, unit, quantity}) {
      const minimum = integer(unit.minimumQuantity) || 1;
      if (!integer(quantity) || quantity < minimum || quantity > 9999) throw error('quantity_invalid');
      await this.loadCategory(categoryId, true);
      const selection = this.selection(categoryId, product.id, unit.code);
      if (selection.status !== 'ready') throw error(selection.status);
      const properties = {'Purchase unit':unit.code, '_catalog_product_id':product.id, '_minimum_quantity':String(minimum)};
      if (product.sku) properties.SKU = String(product.sku);
      if (Number(unit.baseQuantity) > 0 && unit.status !== 'pending') properties['Contents per unit'] = String(unit.baseQuantity) + ' ' + unit.baseUnit;
      await this.request('cart/add.js', {items:[{id:Number(selection.variant.id), quantity, properties}]});
      try { return await this.getCart(); } catch { this.cart=null; throw error('added_refresh_failed'); }
    }
    async change(line, quantity) {
      const minimum = integer(line.properties?._minimum_quantity) || 1;
      if (quantity !== 0 && (!integer(quantity) || quantity < minimum || quantity > 9999)) throw error('quantity_invalid');
      const data = await this.request('cart/change.js', {id:line.key, quantity});
      if (!Array.isArray(data.items)) throw error('request_failed');
      this.cart = data; this.updateBadge(data.item_count); return data;
    }
  }
  window.StoreCatalogCommerce = CatalogCommerce;
})();
